// Source-type registry: the types offered on the Connections page (`sourceTypes`, in the
// order their tiles show) and each type's connect form (`sourceTypeFields`). A new
// source also needs its submit label in components/SourceForm.tsx.
export const sourceTypes = [
    { id: 'box', label: 'Box' },
    { id: 'companies_house', label: 'Companies House' },
    { id: 'docusign', label: 'DocuSign' },
    { id: 'dropbox', label: 'Dropbox' },
    { id: 'gmail', label: 'Gmail' },
    { id: 'drive', label: 'Google Drive' },
    { id: 'hubspot', label: 'HubSpot' },
    { id: 'joiin', label: 'Joiin' },
    { id: 'teams', label: 'Teams' },
    { id: 'notion', label: 'Notion' },
    { id: 'onedrive', label: 'OneDrive' },
    { id: 'outlook', label: 'Outlook' },
    { id: 'sharepoint', label: 'SharePoint' },
    { id: 'slack', label: 'Slack' },
    { id: 'stripe', label: 'Stripe' },
    { id: 'web', label: 'Web' },
    { id: 'xero', label: 'Xero' },
];

// Every workspace has Navaris beside its connected sources: what people upload and what
// Navaris writes. Nothing syncs it, so it has no settings, sync or jobs. The Market
// source, the outside pages Navaris follows, is managed on the Market page.
export const BUILTIN_SOURCES: Record<string, string> = { navaris: 'Navaris', market: 'Market' };
export const isBuiltinSource = (type: string | null | undefined): boolean => !!type && type in BUILTIN_SOURCES;

export const sourceLabel = (id: string | null | undefined): string =>
    id ? (BUILTIN_SOURCES[id] ?? sourceTypes.find(s => s.id === id)?.label ?? id) : '';

type FieldType = 'text' | 'number' | 'password' | 'checkbox' | 'textarea' | 'searches';

interface Field {
    id: string;
    label: string;
    type: FieldType;
    placeholder?: string;
    helperText?: string;
    required?: boolean;
    min?: number;
    max?: number;
    defaultValue?: string | number | boolean;
    documentation?: string;
}

const STRIPE_DOCUMENTATION = `### Connect Stripe with a read-only key

Stripe doesn't offer an OAuth flow for read-only data access outside their Connect
payment-processing platform, so you'll need to paste a **restricted API key** that
you create in your own Stripe dashboard. Takes about two minutes.

---

#### 1. Open the restricted keys page

In a new tab, go to your Stripe dashboard:

\`Dashboard → Developers → API keys → + Create restricted key\`

Or jump directly to: [https://dashboard.stripe.com/apikeys/create](https://dashboard.stripe.com/apikeys/create)

#### 2. Answer "How will you be using this key?"

Stripe asks whether the key is for your own application or for a third party.
Because you'll be giving the key to **Navaris**, choose the option that
indicates you're **sharing the key with a third-party service** (the exact
wording may be *"I'll share this key with a third party"* or similar).

#### 3. Enter website details

Stripe then asks for the third-party's details. Enter:

- **Name:** \`Navaris\`
- **URL:** \`https://www.getnavaris.com\`

(These identify **us** — the service receiving the key — not your own business.)

#### 4. Name the key

Give the key a name **you'll** recognise later, e.g.
**"Navaris - read only"**.

#### 5. Set the required permissions

Under **Permissions**, set the following resources to **Read** (leave everything else as **None**):

- **Core resources → Customers** → Read
- **Core resources → Invoices** → Read
- **Billing resources → Subscriptions** → Read

You may optionally grant:

- **Core resources → Charges** → Read (useful later for one-time revenue)
- **Core resources → Balance transactions** → Read (useful for payout reconciliation)

No **Write** permissions are ever needed. Navaris only reads data.

#### 6. Create and copy the key

Click **Create key**. Stripe will display the new key once — it starts with
\`rk_live_\` (or \`rk_test_\` in test mode). Copy the full key.

> **Important:** Stripe won't show the full key again after you leave this page.
> If you lose it, create a new key and revoke the old one.

#### 7. Paste it here

Paste the key into the **Stripe Restricted API Key** field and save. We'll test
the connection and begin pulling your subscriptions, invoices, and customers.

---

#### Revoking access later

To disconnect Navaris, return to \`Developers → API keys\`, find the key you
named above, and click **Roll key** or **Delete**. Our access stops immediately.

#### Why not a regular OAuth "Connect with Stripe"?

Stripe's OAuth product (Stripe Connect) is designed for platforms that process
payments on behalf of other businesses — it carries commercial and compliance
obligations we don't need. For read-only data access, a restricted key is
Stripe's recommended approach and is the same mechanism used by ChartMogul,
Baremetrics, and most SaaS analytics tools.
`;

const JOIIN_DOCUMENTATION = `### Connect Joiin with an API key

Joiin Connect (Joiin's public API) uses API-key authentication. You generate
the key from inside your own Joiin account and paste it here. Takes about a
minute.

---

#### 1. Open the API access page

In a new tab, log in to Joiin and go to:

\`Integrations → Public API\`

Or jump directly to: [https://app.joiin.co./integrations?tab=api](https://app.joiin.co./integrations?tab=api)

#### 2. Generate a new API key

Click **Generate** (or **+ New Key**) and give the key a name you'll
recognise later, e.g. **"Navaris"**. Joiin will display the key
once — copy the full string.

> **Important:** Joiin won't show the full key again after you leave this
> page. If you lose it, generate a new one and revoke the old.

#### 3. Paste it here

Paste the key into the **Joiin API Key** field below and save.

#### 4. Confirm reporting currency and fiscal year end

The reporting currency tells Joiin which currency to convert all
consolidated reports into (default: \`USD\`). Fiscal year end month tells
us when your financial year closes — \`12\` (December) is most common.

---

#### What data does Navaris read?

Read-only consolidated reports across **all companies the API key has
access to**:

- Trial Balance
- Profit and Loss
- Balance Sheet
- Cashflow

Each is pulled at two granularities: most-recent fiscal year + two priors
(annual), and the trailing twelve months (monthly).

#### Rate limits

Joiin caps API keys at 100 req/s, 200 concurrent, 50,000 req/month. A full
Navaris snapshot uses ~9 requests, so usage is negligible.

#### Revoking access later

To disconnect Navaris, return to \`Integrations → Public API\` in Joiin,
find the key you named above, and **Revoke** it. Our access stops
immediately.
`;

const COMPANIES_HOUSE_DOCUMENTATION = `### Connect a Companies House company

Companies House is the UK's public register of companies. Navaris reads its
free public data on your behalf — there's nothing to log in to. Just tell us
which company to track.

---

#### 1. Find the company number

Search for the company at
[find-and-update.company-information.service.gov.uk](https://find-and-update.company-information.service.gov.uk).
Open the company and copy its **company number** — an 8-character code shown
under the company name (e.g. \`00445790\`, or \`SC123456\` for Scottish
companies).

#### 2. Paste it here

Enter the company number below and save. Numeric numbers shorter than 8 digits
are zero-padded automatically.

---

#### What data does Navaris pull?

- **Company profile** — status, incorporation date, registered office, SIC codes
- **Officers** — directors and secretaries, both active and resigned
- **Persons with significant control** — beneficial owners
- **Filing history** — and the filed **documents** themselves as PDFs:
  certificate of incorporation, annual accounts, confirmation statements,
  officer-appointment / termination forms, charges, and more
- **Charges** and **insolvency** records where they exist

All of this is public information; no credentials or permissions are required.
`;

// Every document store with candidates to file offers this; config.select_documents is
// read by intelligence/candidates.py, and an absent value means on.
const SELECT_DOCUMENTS: Field = {
    id: 'select_documents',
    label: 'File the documents Navaris recommends',
    type: 'checkbox',
    defaultValue: true,
    helperText: 'After each sync Navaris files and reads the documents worth having. Turn this off to see its recommendations on the connection\'s Candidates tab and file them yourself.',
};

// A mail source is read only through its searches. The form starts from the default
// searches (GET /sources/default-queries) and sends the ones kept or added as config.queries.
const SEARCHES: Field = {
    id: 'queries',
    label: 'Searches',
    type: 'searches',
    helperText: 'Navaris reads the mail these searches find at each sync, and nothing else. Remove any that do not fit and add your own; you can change them later on the connection\'s Searches tab.',
};

export const sourceTypeFields: Record<string, Field[]> = {
    companies_house: [
        {
            id: 'source',
            label: 'Account label',
            type: 'text',
            placeholder: 'e.g. Acme Ltd (Companies House)',
            helperText: 'A friendly name so you can recognise this connection later.',
            required: true,
        },
        {
            id: 'company_number',
            label: 'Company Number',
            type: 'text',
            placeholder: 'e.g. 00445790',
            required: true,
            helperText: 'The 8-character Companies House number. See guide for where to find it.',
            documentation: COMPANIES_HOUSE_DOCUMENTATION,
        },
    ],
    web: [
        {
            id: 'source',
            label: 'Source URL',
            type: 'text',
            placeholder: 'https://example.com',
            required: true,
        },
        {
            id: 'use_sitemap',
            label: 'Use Sitemap.xml if available',
            type: 'checkbox',
            defaultValue: true,
        },
        {
            id: 'max_depth',
            label: 'Max Depth',
            type: 'number',
            placeholder: '1',
            helperText: 'Depth of pages to crawl',
            min: 1,
            defaultValue: 1,
        },
        {
            id: 'max_pages',
            label: 'Max Pages',
            type: 'number',
            placeholder: '100',
            helperText: 'Maximum total pages to crawl',
            min: 1,
            defaultValue: 100,
        },
    ],
    gmail: [
        SEARCHES,
        SELECT_DOCUMENTS,
    ],
    outlook: [
        SEARCHES,
        SELECT_DOCUMENTS,
    ],
    drive: [
        {
            id: 'recursive',
            label: 'Include subfolders (recursive)',
            type: 'checkbox',
            defaultValue: true,
            helperText: 'When enabled, includes all files in subfolders.',
        },
        SELECT_DOCUMENTS,
    ],
    onedrive: [
        {
            id: 'recursive',
            label: 'Include subfolders (recursive)',
            type: 'checkbox',
            defaultValue: true,
            helperText: 'When enabled, includes all files in subfolders. When disabled, only indexes files in the specified folder.',
        },
        SELECT_DOCUMENTS,
    ],
    sharepoint: [
        {
            id: 'recursive',
            label: 'Include subfolders (recursive)',
            type: 'checkbox',
            defaultValue: true,
            helperText: 'When enabled, includes all files in subfolders.',
        },
        SELECT_DOCUMENTS,
    ],
    dropbox: [
        {
            id: 'recursive',
            label: 'Include subfolders (recursive)',
            type: 'checkbox',
            defaultValue: true,
            helperText: 'When enabled, includes all files in subfolders.',
        },
        SELECT_DOCUMENTS,
    ],
    box: [
        {
            id: 'recursive',
            label: 'Include subfolders (recursive)',
            type: 'checkbox',
            defaultValue: true,
            helperText: 'When enabled, includes all files in subfolders.',
        },
        SELECT_DOCUMENTS,
    ],
    notion: [SELECT_DOCUMENTS],
    docusign: [SELECT_DOCUMENTS],
    stripe: [
        {
            id: 'source',
            label: 'Account label',
            type: 'text',
            placeholder: 'e.g. Acme Inc. Stripe',
            helperText: 'A friendly name so you can recognise this connection later.',
            required: true,
        },
        {
            id: 'secret_key',
            label: 'Stripe Restricted API Key',
            type: 'password',
            placeholder: 'rk_live_…',
            required: true,
            helperText: 'Read-only restricted key. Create in Stripe Dashboard — see guide for required permissions.',
            documentation: STRIPE_DOCUMENTATION,
        },
    ],
    joiin: [
        {
            id: 'source',
            label: 'Account label',
            type: 'text',
            placeholder: 'e.g. Acme Group Joiin',
            helperText: 'A friendly name so you can recognise this connection later.',
            required: true,
        },
        {
            id: 'api_key',
            label: 'Joiin API Key',
            type: 'password',
            placeholder: 'Paste the key from Integrations → Public API',
            required: true,
            helperText: 'Generated inside your Joiin account. See guide for step-by-step setup.',
            documentation: JOIIN_DOCUMENTATION,
        },
        {
            id: 'currency',
            label: 'Reporting Currency',
            type: 'text',
            placeholder: 'USD',
            defaultValue: 'USD',
            required: true,
            helperText: 'ISO 4217 code. All consolidated reports are converted into this currency.',
        },
        {
            id: 'fy_end_month',
            label: 'Fiscal Year End Month',
            type: 'number',
            placeholder: '12',
            defaultValue: 12,
            min: 1,
            max: 12,
            required: true,
            helperText: 'Month your financial year closes (1 = January, 12 = December).',
        },
    ],
};
